import React, { useState, useEffect, useRef } from 'react'
import quiltType from './quiltType'

function clearCanvas(canvas: HTMLCanvasElement) {
  let n = canvas
  let ntx = n.getContext('2d')
  ntx!.clearRect(0, 0, n.width, n.height)
  ntx!.beginPath()
}

const drawQuilt = ({
  canvas,
  data,
  precision = 1 / 1024,
  bump = false,
  scale = 300,
  color = 'white',
  stitchColor = 'black',
  stitch = false,
  strokeThickness = 1,
  tint = false,
  preview = false,
}: {
  canvas: HTMLCanvasElement
  data: any
  precision?: number
  bump?: boolean
  scale?: number
  color?: string
  stitchColor?: string
  stitch?: boolean
  strokeThickness?: number
  tint?: boolean
  preview?: boolean
}) => {
  const items = data
  const n = canvas
  const p = precision * scale
  n.width = scale
  n.height = scale

  clearCanvas(n)

  const ntx = n.getContext('2d')
  if (ntx) {
    ntx.fillStyle = bump ? 'white' : color
    ntx.fillRect(0, 0, scale, scale)

    ntx.lineJoin = 'round'
    ntx.shadowColor = 'black'
    ntx.lineCap = 'round'

    for (let i = 0; i < items.length; i++) {
      ntx.moveTo(items[i][0][0] * p, items[i][0][1] * p)
      for (let j = 1; j < items[i].length; j++) {
        ntx.lineTo(items[i][j][0] * p, items[i][j][1] * p)
      }
    }

    const r = scale / Math.pow(2, 4) // Randomization factor
    const r2 = r / 2 // half factor

    /* console.log('r', r, 'r2', r2, 'scale', scale) */

    if (bump) {
      /* ntx.filter = 'blur(4px)' */
      let max = 10
      /* const converter = 255 / max */
      for (let i = 0; i <= max; i++) {
        ntx.lineWidth = strokeThickness * 2
        ntx.strokeStyle = `rgba(
                ${0},
                ${0},
                ${0},
                ${1})`

        /* ntx.shadowBlur = Math.pow((Math.random() * scale) / 8, 1.2) */
        ntx.shadowBlur = (Math.pow(Math.random(), 1.5) * scale) / 5
        /* ntx.shadowOffsetX = Math.random() * r - r2
         * ntx.shadowOffsetY = Math.random() * r - r2 */
        ntx.stroke()
      }
    }
    if (tint) {
      let max = 20
      /* const converter = 255 / max */
      for (let i = 0; i <= max; i++) {
        ntx.lineWidth = strokeThickness
        ntx.strokeStyle = `rgba(
                ${0},
                ${0},
                ${0},
                ${preview ? 0.1 : 0.2})`

        ntx.shadowBlur = 0.1 + (Math.pow(Math.random(), 2.8) * scale) / 5
        /* ntx.shadowOffsetX = Math.random() * r - r2
         * ntx.shadowOffsetY = Math.random() * r - r2 */
        ntx.stroke()
      }
    }
    if (stitch) {
      ntx.lineWidth = strokeThickness * 3
      ntx.strokeStyle = preview ? color : '#ffff'
      ntx.stroke()
      ntx.setLineDash([12, 4])
    }

    ntx.lineWidth = strokeThickness * 1.5
    ntx.strokeStyle = stitchColor
    ntx.stroke()
  }
  /* const image = n
     *   .toDataURL('image/png')
     *   .replace('image/png', 'image/octet-stream') // here is the most important part because if you dont replace you will get a DOM 18 exception.

     * window.location.href = image // it will save locally */
}

export const quiltCanvasMaker = ({
  path,
  color = 'black',
  bumpCanvas,
  colorCanvas,
}: {
  path?: string
  color?: string
  bumpCanvas: HTMLCanvasElement
  colorCanvas: HTMLCanvasElement
}) => {
  return new Promise<boolean>((resolve, reject) => {
    if (path) {
      fetch(path)
        .then(f => f.json())
        .then(data => {
          drawQuilt({
            canvas: bumpCanvas,
            data,
            bump: true,
            scale: 512,
          })
          drawQuilt({
            canvas: colorCanvas,
            data,
            scale: 512,
            tint: true,
            stitch: true,
            stitchColor: color,
          })
          resolve(true)
        })
        .catch(err => console.error(err))
    } else {
      resolve(false)
    }
  })
}

const QuiltViewer = ({
  path,
  className,
  onClick,
  bump = false,
  scale = 300,
  color = 'white',
  stitchColor = 'black',
  stitch = false,
  tint = false,
  preview = true,
}: {
  path: string
  bump?: boolean
  scale?: number
  className?: string
  onClick?: () => void
  color?: string
  stitchColor?: string
  stitch?: boolean
  tint?: boolean
  preview?: boolean
}) => {
  const view = useRef<HTMLCanvasElement>(null)
  const [trigger, setTrigger] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [data, setData] = useState<any>(null)
  const [precision, setPrecision] = useState<number>(0.0001)

  useEffect(() => {
    fetch(path)
      .then(e => e.json())
      .then(f => {
        setData(f)
        setTrigger(!trigger)
        setLoading(false)
      })
  }, [path, color, stitchColor])

  useEffect(() => {
    if (!loading && view.current) {
      drawQuilt({
        canvas: view.current,
        data,
        bump,
        scale,
        color,
        stitch,
        stitchColor,
        tint,
        preview,
      })
    }
  }, [trigger, loading, bump, scale, data, path, stitchColor, color])

  return (
    <canvas
      ref={view}
      width={scale}
      height={scale}
      className={className}
      onClick={onClick}
    />
  )
}

export default QuiltViewer
